import { EventEmitter } from 'eventemitter3';
import { 
  ICSElement, 
  ICSElementPattern, 
  ElementOptions, 
  ElementValue, 
  ElementEventType,
  SerializationOptions,
  SerializedElement,
  ValidationResult,
  QuerySelector
} from '../types/interfaces';
import { 
  CSElementPlugin,
  MiddlewareContext,
  PluginOptions,
  PluginManager
} from '../types/plugin-interfaces';
import { 
  HistoryManager, 
  HistoryConfig 
} from '../types/history-interfaces';
import { 
  ReactivityManager, 
  ReactivityConfig, 
  ComputedProperty, 
  ReactiveWatcher, 
  ReactiveRef,
  ComputedFunction,
  WatchCallback,
  ComputedConfig,
  WatcherConfig
} from '../types/reactivity-interfaces';
import { 
  LiveQueryManager,
  LiveQuery,
  LiveQueryConfig,
  LiveQueryBuilder,
  LiveQueryCallback,
  QueryOptions
} from '../types/live-query-interfaces';
import { LiveQueryBuilderImpl } from '../live-queries/LiveQueryManagerImpl';
import { GraphAlgorithmsManager } from '../types/graph-interfaces';
import { PersistenceManager } from '../types/persistence-interfaces';
import { generateId } from '../utils/helpers';
import { AsyncLock } from '../utils/AsyncLock';
import { ElementValidator, defaultValidator } from '../utils/Validator';
import { QueryEngine, CommonSelectors, ObjectSelector, SelectorType } from '../utils/QueryEngine';
import { services } from './ServiceRegistry';
import { ElementNavigation } from './ElementNavigation';
import { ElementSerializer } from './ElementSerializer';

/**
 * Основная реализация CSElement
 * Потокобезопасный класс для работы с графовыми структурами
 */
export class CSElement extends EventEmitter implements ICSElement {
  private _id: string;
  private _name: string;
  private _index: number;
  private _data: Map<string, any>;
  private _elements: Map<string, CSElement>;
  private _elementsByIndex: CSElement[];
  private _owners: Map<string, CSElement>;
  private _mainOwner: CSElement | null;
  private _lock: AsyncLock;
  private _lastElement: CSElement | null;
  
  /**
   * Получить количество всех элементов в системе
   */
  static get totalElementsCount(): number {
    return services.registry.totalElementsCount;
  }

  /**
   * Получить все элементы из глобального реестра
   */
  static getAllElements(): CSElement[] {
    return services.registry.getAllElements();
  }

  constructor(name: string = '', options: ElementOptions = {}) {
    super();
    this._id = generateId();
    this._name = name;
    this._index = options.index ?? 0;
    
    // Правильно обрабатываем данные
    if (options.data instanceof Map) {
      this._data = options.data;
    } else if (options.data && typeof options.data === 'object') {
      this._data = new Map(Object.entries(options.data));
    } else {
      this._data = new Map();
    }
    
    this._elements = new Map();
    this._elementsByIndex = [];
    this._owners = new Map();
    this._mainOwner = null;
    this._lock = new AsyncLock();
    this._lastElement = null;
    
    // Регистрируем элемент в глобальном реестре
    services.registry.register(this);
    
    // Выполняем хук afterCreate синхронно
    services.plugins?.executeHooks('afterCreate', this);
  }

  // Геттеры для readonly свойств
  get id(): string {
    return this._id;
  }

  get name(): string {
    return this._name;
  }

  get index(): number {
    return this._index;
  }

  get data(): ReadonlyMap<string, any> {
    return new Map(this._data);
  }

  get mainOwner(): ICSElementPattern | null {
    return this._mainOwner;
  }

  get lastElement(): ICSElement | null {
    return this._lastElement;
  }

  /**
   * Получить владельца по имени или индексу
   */
  getOwner(nameOrIndex: string | number): ICSElementPattern | null {
    if (typeof nameOrIndex === 'string') {
      return this._owners.get(nameOrIndex) || null;
    } else {
      const owners = Array.from(this._owners.values());
      return owners[nameOrIndex] || null;
    }
  }

  /**
   * Асинхронно получить владельца
   */
  async getOwnerAsync(nameOrIndex: string | number): Promise<ICSElementPattern | null> {
    return this._lock.withLock(async () => {
      return this.getOwner(nameOrIndex);
    });
  }

  /**
   * Получить элемент по имени или индексу
   */
  getElement(nameOrIndex: string | number): ICSElementPattern | null {
    if (typeof nameOrIndex === 'string') {
      return this._elements.get(nameOrIndex) || null;
    } else {
      return this._elementsByIndex[nameOrIndex] || null;
    }
  }

  /**
   * Асинхронно получить элемент
   */
  async getElementAsync(nameOrIndex: string | number): Promise<ICSElementPattern | null> {
    return this._lock.withLock(async () => {
      return this.getElement(nameOrIndex);
    });
  }

  /**
   * Получить относительный индекс для владельца
   */
  getRelativeIndex(owner: ICSElementPattern): number {
    if (!(owner instanceof CSElement)) {
      return -1;
    }
    
    const ownerElement = owner as CSElement;
    const elements = Array.from(ownerElement._elements.values());
    return elements.findIndex(el => el._id === this._id);
  }

  /**
   * Количество дочерних элементов
   */
  elementsCount(): number {
    return this._elementsByIndex.length;
  }

  /**
   * Количество владельцев
   */
  ownersCount(): number {
    return this._owners.size;
  }

  /**
   * Получить все дочерние элементы
   */
  getAllElements(): ReadonlyArray<ICSElementPattern> {
    return Array.from(this._elementsByIndex);
  }

  /**
   * Получить всех владельцев
   */
  getAllOwners(): ReadonlyArray<ICSElementPattern> {
    return Array.from(this._owners.values());
  }

  /**
   * Проверяет наличие дочернего элемента по имени или индексу
   */
  hasElement(nameOrIndex: string | number): boolean {
    if (typeof nameOrIndex === 'string') {
      return this._elements.has(nameOrIndex);
    } else {
      return nameOrIndex >= 0 && nameOrIndex < this._elementsByIndex.length;
    }
  }

  /**
   * Добавить новый элемент
   */
  async addElement(value: ElementValue, options: ElementOptions = {}): Promise<ICSElement> {
    const context: MiddlewareContext = {
      element: this,
      operation: 'addElement',
      args: [value, options],
      metadata: {},
      startTime: Date.now(),
      operationId: generateId(),
      middlewareStack: [],
      flags: {
        aborted: false,
        modified: false,
        inTransaction: false
      }
    };

    const operation = async () => {
      return this._lock.withLock(async () => {
        const element = value instanceof CSElement ? value : new CSElement(value, options);
        
        // Выполняем хук beforeAddElement
        await services.plugins?.executeHooks('beforeAddElement', this, element);
        
        // Устанавливаем имя если не задано
        if (!element._name && options.name) {
          element._name = options.name;
        }
        
        // Добавляем в коллекции
        if (element._name) {
          this._elements.set(element._name, element);
        }
        
        // Устанавливаем индекс
        element._index = options.index ?? this._elementsByIndex.length;
        
        // Добавляем в массив по индексу
        if (element._index >= this._elementsByIndex.length) {
          this._elementsByIndex.push(element);
        } else {
          this._elementsByIndex.splice(element._index, 0, element);
          // Обновляем индексы последующих элементов
          for (let i = element._index + 1; i < this._elementsByIndex.length; i++) {
            this._elementsByIndex[i]._index = i;
          }
        }
        
        // Устанавливаем владельца
        element._owners.set(this._id, this);
        if (!element._mainOwner) {
          element._mainOwner = this;
        }
        
        // Обновляем последний элемент
        this._lastElement = element;
        
        // Записываем операцию в историю
        this.recordOperation('create', `Add element ${element.name || element.id}`, null, {
          id: element.id,
          name: element.name,
          index: element.index
        });
        
        // Генерируем событие
        this.emit(ElementEventType.ElementAdded, element);
        
        // Уведомляем реактивность об изменении дочерних элементов
        this.notifyChildrenChange('add', element);
        
        // Выполняем хук afterAddElement
        await services.plugins?.executeHooks('afterAddElement', this, element);
        
        // Уведомляем Live queries о создании элемента
        element.notifyElementChange('create');
        
        return element;
      });
    };

    // Выполняем через middleware если есть менеджер плагинов
    if (services.plugins) {
      return services.plugins.executeMiddleware(context, operation);
    } else {
      return operation();
    }
  }

  /**
   * Удалить элемент
   */
  async removeElement(nameOrIndex: string | number | ICSElement): Promise<boolean> {
    const context: MiddlewareContext = {
      element: this,
      operation: 'removeElement',
      args: [nameOrIndex],
      metadata: {},
      startTime: Date.now(),
      operationId: generateId(),
      middlewareStack: [],
      flags: {
        aborted: false,
        modified: false,
        inTransaction: false
      }
    };

    const operation = async () => {
      return this._lock.withLock(async () => {
        let element: CSElement | null = null;
        
        if (nameOrIndex instanceof CSElement) {
          element = nameOrIndex;
        } else if (typeof nameOrIndex === 'string' || typeof nameOrIndex === 'number') {
          element = this.getElement(nameOrIndex) as CSElement;
        } else {
          // nameOrIndex is ICSElement but not CSElement
          element = nameOrIndex as CSElement;
        }
        
        if (!element) {
          return false;
        }
        
        // Выполняем хук beforeRemoveElement
        await services.plugins?.executeHooks('beforeRemoveElement', this, element);
        
        // Удаляем из коллекций
        if (element._name) {
          this._elements.delete(element._name);
        }
        
        // Удаляем из массива по индексу
        const index = this._elementsByIndex.indexOf(element);
        if (index !== -1) {
          this._elementsByIndex.splice(index, 1);
          // Обновляем индексы последующих элементов
          for (let i = index; i < this._elementsByIndex.length; i++) {
            this._elementsByIndex[i]._index = i;
          }
        }
        
        // Удаляем владельца у элемента
        element._owners.delete(this._id);
        if (element._mainOwner === this) {
          // Если это был главный владелец, назначаем нового
          const newOwner = element._owners.values().next().value;
          element._mainOwner = newOwner || null;
        }
        
        // Обновляем последний элемент
        if (this._lastElement === element) {
          this._lastElement = this._elementsByIndex[this._elementsByIndex.length - 1] || null;
        }
        
        // Записываем операцию в историю
        this.recordOperation('delete', `Remove element ${element.name || element.id}`, {
          id: element.id,
          name: element.name,
          index: element.index
        }, null);
        
        // Генерируем событие
        this.emit(ElementEventType.ElementRemoved, element);
        
        // Уведомляем реактивность об изменении дочерних элементов
        this.notifyChildrenChange('remove', element);
        
        // Выполняем хук afterRemoveElement
        await services.plugins?.executeHooks('afterRemoveElement', this, element);
        
        // Уведомляем Live queries об удалении элемента
        element.notifyElementChange('delete');
        
        return true;
      });
    };

    // Выполняем через middleware если есть менеджер плагинов
    if (services.plugins) {
      return services.plugins.executeMiddleware(context, operation);
    } else {
      return operation();
    }
  }

  /**
   * Установить данные
   */
  async setData(key: string, value: any): Promise<void> {
    const context: MiddlewareContext = {
      element: this,
      operation: 'setData',
      args: [key, value],
      metadata: {},
      startTime: Date.now(),
      operationId: generateId(),
      middlewareStack: [],
      flags: {
        aborted: false,
        modified: false,
        inTransaction: false
      }
    };

    const operation = async () => {
      return this._lock.withLock(async () => {
        // Используем аргументы из контекста (могут быть модифицированы middleware)
        const [finalKey, finalValue] = context.args;
        
        // Выполняем хук beforeSetData
        await services.plugins?.executeHooks('beforeSetData', this, finalKey, finalValue);
        
        const oldValue = this._data.get(finalKey);
        this._data.set(finalKey, finalValue);
        
        // Записываем операцию в историю
        this.recordOperation('update', `Set data ${finalKey}`, oldValue, finalValue, [finalKey]);
        
        // Уведомляем систему реактивности
        this.notifyDataChange(finalKey, finalValue, oldValue);
        
        // Очищаем кэш QueryEngine, так как данные элемента изменились
        QueryEngine.clearCache();
        
        this.emit(ElementEventType.DataChanged, { key: finalKey, oldValue, newValue: finalValue });
        
        // Выполняем хук afterSetData
        await services.plugins?.executeHooks('afterSetData', this, finalKey, finalValue);
      });
    };

    // Выполняем через middleware если есть менеджер плагинов
    if (services.plugins) {
      return services.plugins.executeMiddleware(context, operation);
    } else {
      return operation();
    }
  }

  /**
   * Получить данные
   */
  getData(key: string): any {
    return this._data.get(key);
  }

  /**
   * Удалить данные
   */
  async deleteData(key: string): Promise<boolean> {
    const context: MiddlewareContext = {
      element: this,
      operation: 'deleteData',
      args: [key],
      metadata: {},
      startTime: Date.now(),
      operationId: generateId(),
      middlewareStack: [],
      flags: {
        aborted: false,
        modified: false,
        inTransaction: false
      }
    };

    const operation = async () => {
      return this._lock.withLock(async () => {
        // Выполняем хук beforeDeleteData
        await services.plugins?.executeHooks('beforeDeleteData', this, key);
        
        const hadKey = this._data.has(key);
        if (hadKey) {
          const oldValue = this._data.get(key);
          this._data.delete(key);
          
          // Записываем операцию в историю
          this.recordOperation('delete', `Delete data ${key}`, oldValue, undefined, [key]);
          
          // Уведомляем систему реактивности
          this.notifyDataChange(key, undefined, oldValue);
          
          this.emit(ElementEventType.DataChanged, { key, oldValue, newValue: undefined });
        }
        
        // Выполняем хук afterDeleteData
        await services.plugins?.executeHooks('afterDeleteData', this, key);
        
        return hadKey;
      });
    };

    // Выполняем через middleware если есть менеджер плагинов
    if (services.plugins) {
      return services.plugins.executeMiddleware(context, operation);
    } else {
      return operation();
    }
  }

  /**
   * Войти в блокировку
   */
  async enterLock(): Promise<void> {
    await this._lock.acquire();
    this.emit(ElementEventType.Locked);
  }

  /**
   * Выйти из блокировки
   */
  async leaveLock(): Promise<void> {
    this._lock.release();
    this.emit(ElementEventType.Unlocked);
  }

  /**
   * Проверить заблокирован ли элемент
   */
  isLocked(): boolean {
    return this._lock.isLocked();
  }

  /**
   * Добавить себя как дочерний элемент к другому элементу
   */
  async addTo(owner: CSElement, options: ElementOptions = {}): Promise<ICSElement> {
    return owner.addElement(this, options);
  }

  /**
   * Удалить себя из владельца
   */
  async removeFrom(owner: CSElement): Promise<boolean> {
    return owner.removeElement(this);
  }

  // ===== Builder API =====

  /**
   * Установить имя элемента (fluent API)
   */
  setName(name: string): this {
    this._name = name;
    return this;
  }

  /**
   * Установить индекс элемента (fluent API)
   */
  setIndex(index: number): this {
    this._index = index;
    return this;
  }

  /**
   * Добавить данные (fluent API)
   */
  withData(key: string, value: any): this {
    this._data.set(key, value);
    return this;
  }

  /**
   * Добавить несколько данных сразу (fluent API)
   */
  withDataObject(data: Record<string, any>): this {
    Object.entries(data).forEach(([key, value]) => {
      this._data.set(key, value);
    });
    return this;
  }

  /**
   * Добавить дочерний элемент (fluent API)
   */
  withChild(nameOrElement: string | CSElement, options?: ElementOptions): this {
    // Используем синхронную версию для fluent API
    const element = nameOrElement instanceof CSElement ? nameOrElement : new CSElement(nameOrElement, options);
    
    // Устанавливаем имя если не задано
    if (!element._name && options?.name) {
      element._name = options.name;
    }
    
    // Добавляем в коллекции
    if (element._name) {
      this._elements.set(element._name, element);
    }
    
    // Устанавливаем индекс
    element._index = options?.index ?? this._elementsByIndex.length;
    
    // Добавляем в массив по индексу
    if (element._index >= this._elementsByIndex.length) {
      this._elementsByIndex.push(element);
    } else {
      this._elementsByIndex.splice(element._index, 0, element);
      // Обновляем индексы последующих элементов
      for (let i = element._index + 1; i < this._elementsByIndex.length; i++) {
        this._elementsByIndex[i]._index = i;
      }
    }
    
    // Устанавливаем владельца
    element._owners.set(this._id, this);
    if (!element._mainOwner) {
      element._mainOwner = this;
    }
    
    // Обновляем последний элемент
    this._lastElement = element;
    
    // Генерируем событие
    this.emit(ElementEventType.ElementAdded, element);
    
    return this;
  }

  /**
   * Добавить несколько дочерних элементов (fluent API)
   */
  withChildren(...names: string[]): this {
    names.forEach(name => {
      this.addElement(name).catch(err => {
        console.error('Failed to add child:', err);
      });
    });
    return this;
  }

  // ===== Статические методы для создания =====

  /**
   * Создать новый элемент с fluent API
   */
  static create(name?: string, options?: ElementOptions): CSElement {
    return new CSElement(name || '', options);
  }

  // ===== Глобальный поиск и навигация =====

  /**
   * Найти элемент по ID глобально
   */
  static getElementById(id: string): CSElement | null {
    return services.registry.getElementById(id);
  }

  /**
   * Найти все элементы по имени глобально
   */
  static getElementsByName(name: string): CSElement[] {
    return services.registry.getElementsByName(name);
  }

  // ===== Система плагинов =====

  /**
   * Установить плагин
   */
  static use(plugin: CSElementPlugin, options?: PluginOptions): void {
    services.plugins.use(plugin, options || undefined);
  }

  /**
   * Удалить плагин
   */
  static uninstall(pluginName: string): boolean {
    return services.plugins?.uninstall(pluginName) || false;
  }

  /**
   * Получить менеджер плагинов
   */
  static get plugins(): PluginManager {
    return services.plugins;
  }

  /**
   * Проверить, установлен ли плагин
   */
  static hasPlugin(name: string): boolean {
    return services.plugins?.hasPlugin(name) || false;
  }

  /**
   * Получить информацию о плагинах
   */
  static getPluginInfo() {
    return services.plugins?.getPluginInfo() || [];
  }

  // ===== Система персистентности =====

  /**
   * Получить менеджер персистентности
   */
  static get persistence(): PersistenceManager {
    return services.persistence;
  }

  /**
   * Сохранить элемент в хранилище
   */
  async save(adapterName?: string, options?: any): Promise<any> {
    return services.persistence.save(this.id, adapterName, options);
  }

  /**
   * Загрузить элемент из хранилища и восстановить его состояние
   */
  static async load(elementId: string, adapterName?: string, options?: any): Promise<CSElement | null> {
    const result = await services.persistence.load(elementId, adapterName, options);
    
    if (result.success && result.data) {
      // Десериализуем элемент
      const element = CSElement.deserialize(result.data);
      return element;
    }

    return null;
  }

  /**
   * Удалить элемент из хранилища
   */
  async deleteFromStorage(adapterName?: string, options?: any): Promise<any> {
    return services.persistence.delete(this.id, adapterName, options);
  }

  /**
   * Найти все элементы по условию
   */
  async findElements(predicate: (element: CSElement) => boolean): Promise<CSElement[]> {
    return ElementNavigation.findElements(this, predicate as any) as Promise<CSElement[]>;
  }

  /**
   * Найти первый элемент по условию
   */
  async findElement(predicate: (element: CSElement) => boolean): Promise<CSElement | null> {
    return ElementNavigation.findElement(this, predicate as any) as Promise<CSElement | null>;
  }

  /**
   * Обход всех элементов в глубину
   */
  async traverseDepthFirst(
    callback: (element: CSElement) => void | Promise<void>,
    includeThis: boolean = true
  ): Promise<void> {
    return ElementNavigation.traverseDepthFirst(this, callback as any, includeThis);
  }

  /**
   * Обход всех элементов в ширину
   */
  async traverseBreadthFirst(
    callback: (element: CSElement) => void | Promise<void>,
    includeThis: boolean = true
  ): Promise<void> {
    return ElementNavigation.traverseBreadthFirst(this, callback as any, includeThis);
  }

  /**
   * Получить путь до элемента
   */
  getPath(): string[] {
    return ElementNavigation.getPath(this);
  }

  /**
   * Получить глубину элемента
   */
  getDepth(): number {
    return ElementNavigation.getDepth(this);
  }

  /**
   * Экспорт структуры в JSON
   */
  toJSON(includeData: boolean = true): any {
    return ElementSerializer.toJSON(this, includeData);
  }

  /**
   * Клонировать элемент со всей структурой
   */
  async clone(deep: boolean = true): Promise<CSElement> {
    const cloned = new CSElement(this.name, {
      data: new Map(this._data),
      index: this.index
    });

    if (deep) {
      for (const child of this.getAllElements()) {
        if (child instanceof CSElement) {
          const clonedChild = await child.clone(deep);
          await cloned.addElement(clonedChild);
        }
      }
    }

    return cloned;
  }

  /**
   * Удалить элемент из глобального реестра (вызывается при уничтожении)
   */
  destroy(): void {
    // Выполняем хук beforeDestroy синхронно
    services.plugins?.executeHooks('beforeDestroy', this);

    // Удаляем из всех владельцев
    this.getAllOwners().forEach(owner => {
      if (owner instanceof CSElement) {
        owner.removeElement(this);
      }
    });

    // Рекурсивно удаляем все дочерние элементы
    this.getAllElements().forEach(child => {
      if (child instanceof CSElement) {
        child.destroy();
      }
    });

    // Удаляем из глобального реестра
    services.registry.unregister(this);

    // Удаляем все слушатели событий
    this.removeAllListeners();

    // Выполняем хук afterDestroy синхронно
    services.plugins?.executeHooks('afterDestroy', this);
  }

  /**
   * Получить статистику элемента
   */
  getStats(): {
    totalElements: number;
    maxDepth: number;
    totalData: number;
  } {
    let totalElements = 0;
    let maxDepth = 0;
    let totalData = 0;
    
    const visited = new Set<string>();
    
    const traverse = (element: CSElement, currentDepth: number = 0) => {
      if (visited.has(element.id)) return;
      visited.add(element.id);
      
      totalElements++;
      maxDepth = Math.max(maxDepth, currentDepth);
      totalData += element.data.size;
      
      element.getAllElements().forEach(child => {
        if (child instanceof CSElement) {
          traverse(child, currentDepth + 1);
        }
      });
    };
    
    traverse(this, 0);

    return { totalElements, maxDepth, totalData };
  }

  /**
   * Проверить является ли элемент предком
   */
  isAncestorOf(element: CSElement): boolean {
    let current: CSElement | null = element;

    while (current) {
      if (current === this) return true;
      current = current.mainOwner as CSElement | null;
    }

    return false;
  }

  /**
   * Проверить является ли элемент потомком
   */
  isDescendantOf(element: CSElement): boolean {
    return element.isAncestorOf(this);
  }

  // ===== Расширенная сериализация =====

  /**
   * Улучшенная сериализация с опциями
   */
  serialize(options: SerializationOptions = {}): SerializedElement {
    return ElementSerializer.serialize(this, options);
  }

  /**
   * Десериализация из объекта
   */
  static deserialize(data: SerializedElement): CSElement {
    const element = new CSElement(data.name || '', {
      index: data.index
    });

    // Восстанавливаем оригинальный ID если он есть
    if (data.id) {
      // Удаляем из глобального реестра текущий ID
      services.registry.unregister(element);
      // Устанавливаем новый ID
      element._id = data.id;
      // Регистрируем с новым ID
      services.registry.register(element);
    }

    // Восстанавливаем данные
    if (data.data) {
      Object.entries(data.data).forEach(([key, value]) => {
        element._data.set(key, value);
      });
    }

    // Рекурсивно восстанавливаем дочерние элементы
    if (data.children) {
      data.children.forEach(childData => {
        const child = CSElement.deserialize(childData);
        element.withChild(child);
      });
    }

    return element;
  }

  // ===== Валидация =====

  /**
   * Валидирует элемент используя глобальный валидатор
   */
  validate(elementType: string = 'default'): ValidationResult {
    return defaultValidator.validate(this, elementType);
  }

  /**
   * Валидирует элемент используя кастомный валидатор
   */
  validateWith(validator: ElementValidator, elementType: string = 'default'): ValidationResult {
    return validator.validate(this, elementType);
  }

  // ===== Система запросов =====

  /**
   * Поиск элементов по селектору
   */
  query(selector: string | QuerySelector): CSElement[] {
    if (typeof selector === 'string') {
      return QueryEngine.query(this, selector) as CSElement[];
    }
    const objectSelector: ObjectSelector = { type: SelectorType.OBJECT, selector: selector };
    return QueryEngine.query(this, objectSelector) as CSElement[];
  }

  /**
   * Поиск первого элемента по селектору
   */
  queryOne(selector: string | QuerySelector): CSElement | null {
    if (typeof selector === 'string') {
      return QueryEngine.queryOne(this, selector) as CSElement | null;
    }
    const objectSelector: ObjectSelector = { type: SelectorType.OBJECT, selector: selector };
    return QueryEngine.queryOne(this, objectSelector) as CSElement | null;
  }

  /**
   * Проверяет, соответствует ли элемент селектору
   */
  matches(selector: string | QuerySelector): boolean {
    if (typeof selector === 'string') {
      return QueryEngine.query(this, selector).includes(this);
    }
    const objectSelector: ObjectSelector = { type: SelectorType.OBJECT, selector: selector };
    const results = QueryEngine.query(this, objectSelector);
    return results.includes(this);
  }

  // ===== Статические методы для работы с селекторами =====

  /**
   * Создает селектор для поиска
   */
  static createSelector() {
    return QueryEngine.createSelector();
  }

  /**
   * Получает предопределенные селекторы
   */
  static get selectors() {
    return CommonSelectors;
  }

  // ===== Утилиты для работы с данными =====

  /**
   * Массовое обновление данных с валидацией
   */
  async updateData(updates: Record<string, any>, validate: boolean = false): Promise<ValidationResult | null> {
    return this._lock.withLock(async () => {
      // Сначала валидируем если требуется
      if (validate) {
        // Создаем временную копию для валидации
        const tempElement = await this.clone(false);
        Object.entries(updates).forEach(([key, value]) => {
          tempElement._data.set(key, value);
        });
        
        const validationResult = tempElement.validate();
        if (!validationResult.isValid) {
          return validationResult;
        }
      }

      // Применяем изменения
      const oldValues: Record<string, any> = {};
      Object.entries(updates).forEach(([key, value]) => {
        oldValues[key] = this._data.get(key);
        this._data.set(key, value);
      });

      // Генерируем событие
      this.emit(ElementEventType.DataChanged, { 
        updates, 
        oldValues, 
        newValues: updates 
      });

      return null; // Валидация прошла успешно
    });
  }

  /**
   * Получает все данные как обычный объект
   */
  getDataAsObject(): Record<string, any> {
    return Object.fromEntries(this._data);
  }

  /**
   * Проверяет наличие данных по ключу
   */
  hasData(key: string): boolean {
    return this._data.has(key);
  }

  /**
   * Получает размер данных
   */
  getDataSize(): number {
    return this._data.size;
  }

  /**
   * Очищает все данные
   */
  async clearData(): Promise<void> {
    return this._lock.withLock(async () => {
      const oldData = this.getDataAsObject();
      this._data.clear();
      
      this.emit(ElementEventType.DataChanged, { 
        cleared: true, 
        oldData 
      });
    });
  }

  // === МЕТОДЫ РАБОТЫ С ИСТОРИЕЙ ===

  /**
   * Получить менеджер истории
   */
  static get history(): HistoryManager {
    return services.history;
  }

  /**
   * Настроить систему истории
   */
  static configureHistory(config: HistoryConfig): void {
    services.configureHistory(config);
  }

  /**
   * Отменить последнюю операцию
   */
  static async undo(): Promise<any> {
    return services.history.undo();
  }

  /**
   * Повторить отмененную операцию
   */
  static async redo(): Promise<any> {
    return services.history.redo();
  }

  /**
   * Отменить до определенной операции
   */
  static async undoTo(operationId: string): Promise<any> {
    return services.history.undoTo(operationId);
  }

  /**
   * Повторить до определенной операции
   */
  static async redoTo(operationId: string): Promise<any> {
    return services.history.redoTo(operationId);
  }

  /**
   * Получить состояние истории
   */
  static getHistoryState() {
    return services.history.getState();
  }

  /**
   * Получить операции истории
   */
  static getHistoryOperations(limit?: number) {
    return services.history.getOperations(limit);
  }

  /**
   * Получить снимки истории
   */
  static getHistorySnapshots(limit?: number) {
    return services.history.getSnapshots(limit);
  }

  /**
   * Создать снимок текущего состояния элемента
   */
  createSnapshot(description: string = 'Manual snapshot') {
    const data = {
      id: this._id,
      name: this._name,
      index: this._index,
      data: Object.fromEntries(this._data),
      elements: this._elementsByIndex.map(el => el.id),
      owners: Array.from(this._owners.keys())
    };
    
    return services.history.createSnapshot(data, description);
  }

  /**
   * Очистить историю
   */
  static clearHistory(): void {
    services.history.clear();
  }

  /**
   * Экспорт истории
   */
  static exportHistory() {
    return services.history.export();
  }

  /**
   * Импорт истории
   */
  static importHistory(data: any): void {
    services.history.import(data);
  }

  /**
   * Подписка на события истории
   */
  static onHistoryEvent(event: string, callback: Function): void {
    services.history.on(event as any, callback as any);
  }

  /**
   * Отписка от событий истории
   */
  static offHistoryEvent(event: string, callback: Function): void {
    services.history.off(event as any, callback as any);
  }

  // Приватные методы для записи операций в историю

  private recordOperation(type: string, description: string, before: any, after: any, path?: string[]): void {
    try {
      services.history.addOperation({
        type: type as any,
        before,
        after,
        path,
        description,
        canUndo: true,
        canRedo: true,
        metadata: {
          elementId: this._id,
          elementName: this._name,
          timestamp: Date.now()
        }
      });
    } catch (error) {
      console.warn('Не удалось записать операцию в историю:', error);
    }
  }

  // ==================== СИСТЕМА РЕАКТИВНОСТИ ====================

  /**
   * Получить глобальный менеджер реактивности
   */
  static get reactivity(): ReactivityManager {
    return services.reactivity;
  }

  /**
   * Настроить систему реактивности
   */
  static configureReactivity(config: ReactivityConfig): void {
    services.configureReactivity(config);
  }

  /**
   * Создать computed свойство
   */
  static computed<T>(compute: ComputedFunction<T>, config?: ComputedConfig): ComputedProperty<T> {
    return services.reactivity.computed(compute, config);
  }

  /**
   * Создать наблюдатель за изменениями
   */
  static watch(path: string | string[], callback: WatchCallback, config?: WatcherConfig): ReactiveWatcher {
    return services.reactivity.watch(path, callback, config);
  }

  /**
   * Удалить наблюдатель
   */
  static unwatch(watcherId: string): boolean {
    return services.reactivity.unwatch(watcherId);
  }

  /**
   * Получить значение computed свойства
   */
  static getComputedValue<T>(computedId: string): T | undefined {
    return services.reactivity.getComputedValue<T>(computedId);
  }

  /**
   * Создать реактивную ссылку
   */
  static ref<T>(value: T): ReactiveRef<T> {
    return services.reactivity.ref(value);
  }

  /**
   * Создать реактивный объект
   */
  static reactive<T extends object>(obj: T): T {
    return services.reactivity.reactive(obj);
  }

  /**
   * Получить граф зависимостей
   */
  static getDependencyGraph() {
    return services.reactivity.getDependencyGraph();
  }

  /**
   * Получить статистику реактивности
   */
  static getReactivityStats() {
    return services.reactivity.getStats();
  }

  /**
   * Очистить все реактивные данные
   */
  static clearReactivity(): void {
    services.reactivity.clear();
  }

  /**
   * Подписаться на события реактивности
   */
  static onReactivityEvent(event: string, callback: Function): void {
    services.reactivity.on(event as any, callback as any);
  }

  /**
   * Отписаться от событий реактивности
   */
  static offReactivityEvent(event: string, callback: Function): void {
    services.reactivity.off(event as any, callback as any);
  }

  // === AUTO-DISPOSE МЕТОДЫ ===

  /**
   * Создать новую область видимости
   */
  static createScope(parentId?: string) {
    return services.reactivity.createScope(parentId);
  }

  /**
   * Выполнить функцию в контексте области видимости
   */
  static runInScope<T>(scopeId: string, fn: () => T): T {
    return services.reactivity.runInScope(scopeId, fn);
  }

  /**
   * Очистить область видимости и все её дочерние элементы
   */
  static disposeScope(scopeId: string): void {
    services.reactivity.disposeScope(scopeId);
  }

  /**
   * Очистить computed и все его дочерние элементы
   */
  static disposeComputed(computedId: string): void {
    services.reactivity.disposeComputed(computedId);
  }

  /**
   * Очистить watcher и все его дочерние элементы
   */
  static disposeWatcher(watcherId: string): void {
    services.reactivity.disposeWatcher(watcherId);
  }

  /**
   * Получить текущий контекст выполнения
   */
  static getCurrentContext() {
    return services.reactivity.getCurrentContext();
  }

  /**
   * Установить родителя для auto-dispose
   */
  static setParent(childId: string, parentId: string, type: 'computed' | 'watcher'): void {
    services.reactivity.setParent(childId, parentId, type);
  }

  /**
   * Получить всех детей элемента
   */
  static getChildren(parentId: string, type?: 'computed' | 'watcher'): string[] {
    return services.reactivity.getChildren(parentId, type);
  }

  /**
   * Проверить, был ли элемент disposed
   */
  static isDisposed(id: string, type: 'computed' | 'watcher'): boolean {
    return services.reactivity.isDisposed(id, type);
  }

  // Методы экземпляра для работы с реактивностью

  /**
   * Создать computed свойство для этого элемента
   */
  computed<T>(compute: ComputedFunction<T>, config?: ComputedConfig): ComputedProperty<T> {
    return services.reactivity.computed(compute, config);
  }

  /**
   * Создать наблюдатель за свойством этого элемента
   */
  watch(property: string, callback: WatchCallback, config?: WatcherConfig): ReactiveWatcher {
    const path = [this._id, 'data', property];
    return services.reactivity.watch(path, callback, config);
  }

  /**
   * Наблюдать за дочерними элементами
   */
  watchChildren(callback: WatchCallback, config?: WatcherConfig): ReactiveWatcher {
    const path = [this.id, 'children'];
    return services.reactivity.watch(path, callback, config);
  }

  /**
   * Создать реактивную версию данных элемента
   */
  makeReactive(): this {
    // Создаем реактивную версию данных
    const reactiveData = services.reactivity.reactive(Object.fromEntries(this._data));
    
    // Заменяем Map на реактивный объект
    this._data.clear();
    for (const [key, value] of Object.entries(reactiveData)) {
      this._data.set(key, value);
    }

    return this;
  }

  // Приватные методы для уведомления системы реактивности

  private notifyDataChange(key: string, newValue: any, oldValue: any): void {
    // Уведомляем глобальный менеджер
    services.reactivity.notify([this.id, 'data', key], newValue, oldValue);
    services.liveQueries.notifyDataChange(this, key, newValue, oldValue);
  }

  private notifyChildrenChange(action: 'add' | 'remove', element: CSElement): void {
    // Уведомляем об изменении коллекции дочерних элементов
    const changeInfo = { action, element: element.id };
    services.reactivity.notify([this.id, 'children'], changeInfo, null);
  }

  // ==================== LIVE QUERIES API ====================
  
  /**
   * Получить менеджер Live queries
   */
  static get liveQueries(): LiveQueryManager {
    return services.liveQueries;
  }
  
  /**
   * Получить менеджер алгоритмов работы с графами
   */
  static get graphAlgorithms(): GraphAlgorithmsManager {
    return services.graphAlgorithms;
  }

  /**
   * Создать live query
   */
  static createLiveQuery<T = any>(
    selector: string,
    options?: QueryOptions,
    config?: LiveQueryConfig
  ): LiveQuery<T> {
    return services.liveQueries.createLiveQuery<T>(selector, options, config);
  }

  /**
   * Получить builder для live query
   */
  static queryBuilder<T = any>(): LiveQueryBuilder<T> {
    return new LiveQueryBuilderImpl<T>(services.liveQueries);
  }

  /**
   * Подписаться на live query
   */
  static subscribeLiveQuery<T>(queryId: string, callback: LiveQueryCallback<T>): string {
    return services.liveQueries.subscribe(queryId, callback);
  }

  /**
   * Отписаться от live query
   */
  static unsubscribeLiveQuery(queryId: string, subscriptionId: string): boolean {
    return services.liveQueries.unsubscribe(queryId, subscriptionId);
  }

  /**
   * Запустить live query
   */
  static startLiveQuery(queryId: string): void {
    services.liveQueries.start(queryId);
  }

  /**
   * Остановить live query
   */
  static stopLiveQuery(queryId: string): void {
    services.liveQueries.stop(queryId);
  }

  /**
   * Получить live query
   */
  static getLiveQuery(queryId: string): LiveQuery | undefined {
    return services.liveQueries.getLiveQuery(queryId);
  }

  /**
   * Получить все live queries
   */
  static getAllLiveQueries(): LiveQuery[] {
    return services.liveQueries.getAllLiveQueries();
  }

  /**
   * Удалить live query
   */
  static removeLiveQuery(queryId: string): boolean {
    return services.liveQueries.removeLiveQuery(queryId);
  }

  /**
   * Получить статистику live query
   */
  static getLiveQueryStats() {
    return services.liveQueries.getStats();
  }

  /**
   * Очистить все live query
   */
  static clearLiveQueries(): void {
    services.liveQueries.clear();
  }

  /**
   * Обновить все live query
   */
  static updateAllLiveQueries(): void {
    services.liveQueries.updateAllQueries();
  }

  /**
   * Подписаться на события live query
   */
  static onLiveQueryEvent(callback: (event: any) => void): void {
    services.liveQueries.on('event', callback as any);
  }

  /**
   * Отписаться от событий live query
   */
  static offLiveQueryEvent(callback: (event: any) => void): void {
    services.liveQueries.off('event', callback as any);
  }

  // Методы экземпляра для live query

  /**
   * Создать live query для дочерних элементов
   */
  createChildrenLiveQuery<T = any>(
    selector: string = '*',
    options?: QueryOptions,
    config?: LiveQueryConfig
  ): LiveQuery<T> {
    // Модифицируем селектор для поиска только среди дочерних элементов
    const childSelector = `#${this._id} > ${selector}`;
    return services.liveQueries.createLiveQuery<T>(childSelector, options || {}, config);
  }

  /**
   * Создать live query для всех потомков
   */
  createDescendantsLiveQuery<T = any>(
    selector: string = '*',
    options?: QueryOptions,
    config?: LiveQueryConfig
  ): LiveQuery<T> {
    // Модифицируем селектор для поиска среди всех потомков
    const descendantSelector = `#${this._id} ${selector}`;
    return services.liveQueries.createLiveQuery<T>(descendantSelector, options || {}, config);
  }

  /**
   * Наблюдать за дочерними элементами
   */
  watchChildrenLive<T = any>(callback: LiveQueryCallback<T>, config?: LiveQueryConfig): LiveQuery<T> {
    const query = services.liveQueries.createLiveQuery<T>('> *', { root: this }, config);
    services.liveQueries.subscribe(query.id, callback);
    return query;
  }

  /**
   * Наблюдать за элементами с определенными данными
   */
  watchElementsWithData<T = any>(
    dataKey: string,
    callback: LiveQueryCallback<T>,
    config?: LiveQueryConfig
  ): LiveQuery<T> {
    const query = services.liveQueries.createLiveQuery<T>(`[${dataKey}]`, { root: this, deep: true }, config);
    services.liveQueries.subscribe(query.id, callback);
    return query;
  }

  /**
   * Уведомить live query об изменении элемента
   */
  private notifyElementChange(changeType: 'create' | 'update' | 'delete'): void {
    // Уведомляем глобальный менеджер
    services.liveQueries.notifyElementChange(this, changeType);
  }

  // ==================== АЛГОРИТМЫ ГРАФОВ ====================

  /**
   * Найти путь между узлами
   */
  static async findPath(from: CSElement, to: CSElement, options?: any) {
    return services.graphAlgorithms.findPath(from, to, options);
  }

  /**
   * Найти кратчайший путь
   */
  static async findShortestPath(from: CSElement, to: CSElement, options?: any) {
    return services.graphAlgorithms.findShortestPath(from, to, options);
  }

  /**
   * Найти все пути
   */
  static async findAllPaths(from: CSElement, to: CSElement, options?: any) {
    return services.graphAlgorithms.findAllPaths(from, to, options);
  }

  /**
   * Обнаружить циклы
   */
  static async detectCycles(root: CSElement, options?: any) {
    return services.graphAlgorithms.detectCycles(root, options);
  }

  /**
   * Проверить наличие циклов
   */
  static async hasCycle(root: CSElement, options?: any): Promise<boolean> {
    return services.graphAlgorithms.hasCycle(root, options);
  }

  /**
   * Найти цикл
   */
  static async findCycle(root: CSElement, options?: any) {
    return services.graphAlgorithms.findCycle(root, options);
  }

  /**
   * Найти компоненты связности
   */
  static async findConnectedComponents(root: CSElement, options?: any) {
    return services.graphAlgorithms.findConnectedComponents(root, options);
  }

  /**
   * Найти сильно связанные компоненты
   */
  static async findStronglyConnectedComponents(root: CSElement, options?: any) {
    return services.graphAlgorithms.findStronglyConnectedComponents(root, options);
  }

  /**
   * Проверить связность
   */
  static async isConnected(root: CSElement, options?: any): Promise<boolean> {
    return services.graphAlgorithms.isConnected(root, options);
  }

  /**
   * Построить представление графа
   */
  static async buildGraph(root: CSElement, options?: any) {
    return services.graphAlgorithms.buildGraph(root, options);
  }

  /**
   * Проанализировать граф
   */
  static async analyzeGraph(root: CSElement, options?: any) {
    return services.graphAlgorithms.analyzeGraph(root, options);
  }

  /**
   * Получить статистику графа
   */
  static async getGraphStatistics(root: CSElement, options?: any) {
    return services.graphAlgorithms.getGraphStatistics(root, options);
  }

  /**
   * Получить расстояние между узлами
   */
  static async getDistance(from: CSElement, to: CSElement, options?: any): Promise<number> {
    return services.graphAlgorithms.getDistance(from, to, options);
  }

  /**
   * Получить соседей
   */
  static getNeighbors(node: CSElement, options?: any): CSElement[] {
    return services.graphAlgorithms.getNeighbors(node, options);
  }

  /**
   * Получить степень вершины
   */
  static getDegree(node: CSElement, options?: any): number {
    return services.graphAlgorithms.getDegree(node, options);
  }

  /**
   * Топологическая сортировка
   */
  static async topologicalSort(root: CSElement, options?: any) {
    return services.graphAlgorithms.topologicalSort(root, options);
  }

  /**
   * Минимальное остовное дерево
   */
  static async minimumSpanningTree(root: CSElement, options?: any) {
    return services.graphAlgorithms.minimumSpanningTree(root, options);
  }

  /**
   * Рассчитать центральность
   */
  static async calculateCentrality(root: CSElement, options?: any) {
    return services.graphAlgorithms.calculateCentrality(root, options);
  }

  /**
   * Получить метрики производительности
   */
  static getGraphAlgorithmMetrics() {
    return services.graphAlgorithms.getMetrics();
  }

  /**
   * Очистить кэш алгоритмов
   */
  static clearGraphAlgorithmCache(): void {
    services.graphAlgorithms.clearCache();
  }

  /**
   * Настроить алгоритмы
   */
  static configureGraphAlgorithms(config: any): void {
    services.graphAlgorithms.configure(config);
  }

  /**
   * Подписаться на события
   */
  static onGraphAlgorithmEvent(callback: (event: any) => void): void {
    services.graphAlgorithms.on('algorithm-event', callback as any);
  }

  /**
   * Отписаться от событий
   */
  static offGraphAlgorithmEvent(callback: (event: any) => void): void {
    services.graphAlgorithms.off('algorithm-event', callback as any);
  }

  // Методы экземпляра для работы с алгоритмами

  /**
   * Найти путь до другого элемента
   */
  async findPathTo(target: CSElement, options?: any) {
    return services.graphAlgorithms.findPath(this, target, options);
  }

  /**
   * Найти кратчайший путь до другого элемента
   */
  async findShortestPathTo(target: CSElement, options?: any) {
    return services.graphAlgorithms.findShortestPath(this, target, options);
  }

  /**
   * Найти все пути до другого элемента
   */
  async findAllPathsTo(target: CSElement, options?: any) {
    return services.graphAlgorithms.findAllPaths(this, target, options);
  }

  /**
   * Обнаружить циклы начиная отсюда
   */
  async detectCyclesFromHere(options?: any) {
    return services.graphAlgorithms.detectCycles(this, options);
  }

  /**
   * Проверить наличие циклов отсюда
   */
  async hasCycleFromHere(options?: any): Promise<boolean> {
    return services.graphAlgorithms.hasCycle(this, options);
  }

  /**
   * Найти компоненты связности отсюда
   */
  async findConnectedComponentsFromHere(options?: any) {
    return services.graphAlgorithms.findConnectedComponents(this, options);
  }

  /**
   * Проверить связность отсюда
   */
  async isConnectedFromHere(options?: any): Promise<boolean> {
    return services.graphAlgorithms.isConnected(this, options);
  }

  /**
   * Построить граф отсюда
   */
  async buildGraphFromHere(options?: any) {
    return services.graphAlgorithms.buildGraph(this, options);
  }

  /**
   * Проанализировать граф отсюда
   */
  async analyzeGraphFromHere(options?: any) {
    return services.graphAlgorithms.analyzeGraph(this, options);
  }

  /**
   * Получить статистику графа отсюда
   */
  async getGraphStatisticsFromHere(options?: any) {
    return services.graphAlgorithms.getGraphStatistics(this, options);
  }

  /**
   * Получить расстояние до другого элемента
   */
  async getDistanceTo(target: CSElement, options?: any): Promise<number> {
    return services.graphAlgorithms.getDistance(this, target, options);
  }

  /**
   * Получить соседей отсюда
   */
  getNeighborsFromHere(options?: any): CSElement[] {
    return services.graphAlgorithms.getNeighbors(this, options);
  }

  /**
   * Получить степень отсюда
   */
  getDegreeFromHere(options?: any): number {
    return services.graphAlgorithms.getDegree(this, options);
  }

  /**
   * Топологическая сортировка отсюда
   */
  async topologicalSortFromHere(options?: any) {
    return services.graphAlgorithms.topologicalSort(this, options);
  }

  /**
   * Минимальное остовное дерево отсюда
   */
  async minimumSpanningTreeFromHere(options?: any) {
    return services.graphAlgorithms.minimumSpanningTree(this, options);
  }

  /**
   * Рассчитать центральность для этого узла
   */
  async calculateCentralityFromHere(options?: any) {
    return services.graphAlgorithms.calculateCentrality(this, options);
  }

  /**
   * Преобразует элемент и его потомков в простой объект для JSPath
   */
  toJSPathObject(): any {
    const obj: any = {
      ___id: this.id, // Используем нестандартное имя, чтобы избежать конфликтов
      name: this.name,
      index: this.index,
      data: Object.fromEntries(this.data)
    };

    const children = this.getAllElements();
    if (children.length > 0) {
      obj.children = children.map(child => (child as CSElement).toJSPathObject());
    }

    return obj;
  }
} 